// src/services/sessionListeners.js
//
// THE way the app opens a Firestore listener (docs/bugs/idle_signout_listener_leak_BUG.md). A drop-in for
// `onSnapshot` from 'firebase/firestore' (same signatures, same return value) that registers every live listener
// with the signed-in session, so the session can close them all at once BEFORE auth goes away (sessionEnd.js).
// Without it, a sign-out left each store's listeners running into "Missing or insufficient permissions".
//
// A listener that errors is dropped here at once: Firestore has already stopped it for good. The unsubscribe this
// returns also answers `active` (false once it was unsubscribed, errored, or closed by the session), so a store can
// tell a live listener from a dead one instead of trusting that it still holds a handle.
//
// It is also THE way the app terminates Firestore (releaseFirestore): the tracked listeners close first, so a
// cache-rebuild park or reset no longer makes every open listener report "Firestore shutting down" as an error
// (~40 per parked tab, docs/bugs/cache_park_listener_errors_BUG.md).
//
// `npm run test:session-listeners` fails on any other import of onSnapshot or terminate from 'firebase/firestore'.

import { onSnapshot as firestoreOnSnapshot, terminate } from 'firebase/firestore'

const live = new Set()

// Firestore's own shutdown (terminate(), or the SDK terminating itself when another tab deletes the shared cache)
// errors every open listener with this. It is not the listener's failure: the tab's Firestore is gone and the page
// reloads. Such a listener closes quietly, and one line per shutdown says what happened.
const isFirestoreShutdown = error =>
  error?.code === 'aborted' && /Firestore shutting down/.test(String(error?.message || ''))
let shutdownNote = null
function noteShutdown() {
  if (shutdownNote) {
    shutdownNote.count += 1
    return
  }
  shutdownNote = { count: 1 }
  setTimeout(() => {
    console.info(
      `[sessionListeners] Firestore shut down in this tab; ${shutdownNote.count} listener(s) closed`
    )
    shutdownNote = null
  }, 0)
}

const isObserver = x =>
  !!x &&
  typeof x === 'object' &&
  ['next', 'error', 'complete'].some(k => typeof x[k] === 'function')

/**
 * onSnapshot(reference, onNext, onError?, onCompletion?)
 * onSnapshot(reference, options, onNext, onError?, onCompletion?)
 * onSnapshot(reference, observer)
 * onSnapshot(reference, options, observer)
 *
 * @returns {(() => void) & { active: boolean }} unsubscribe
 */
export function onSnapshot(reference, ...args) {
  const entry = { open: true, stop: null }
  const drop = () => {
    entry.open = false
    live.delete(entry)
  }
  // Firestore stops a listener after its error callback; forget it before handing the error on. With no handler of
  // its own, the error is logged the way Firestore logs an unhandled one.
  const failed = handler => error => {
    drop()
    if (isFirestoreShutdown(error)) return noteShutdown()
    if (typeof handler === 'function') handler(error)
    else console.error('Uncaught Error in snapshot listener:', error)
  }

  const lead = []
  let rest = args
  if (rest.length && typeof rest[0] !== 'function' && !isObserver(rest[0])) {
    lead.push(rest[0]) // SnapshotListenOptions, e.g. { includeMetadataChanges: true }
    rest = rest.slice(1)
  }
  let callbacks
  if (isObserver(rest[0])) {
    const observer = rest[0]
    callbacks = [
      {
        next: typeof observer.next === 'function' ? value => observer.next(value) : undefined,
        error: failed(
          typeof observer.error === 'function' ? error => observer.error(error) : undefined
        ),
        complete: typeof observer.complete === 'function' ? () => observer.complete() : undefined,
      },
    ]
  } else {
    const [next, error, complete] = rest
    callbacks = complete === undefined ? [next, failed(error)] : [next, failed(error), complete]
  }

  entry.stop = firestoreOnSnapshot(reference, ...lead, ...callbacks)
  if (entry.open) live.add(entry)

  const unsubscribe = () => {
    drop()
    entry.stop()
  }
  Object.defineProperty(unsubscribe, 'active', { get: () => entry.open, enumerable: true })
  return unsubscribe
}

/**
 * Close every listener this session opened (sessionEnd.js calls it before signing out). Returns how many were open.
 */
export function closeAllListeners() {
  const entries = [...live]
  live.clear()
  for (const entry of entries) {
    entry.open = false
    try {
      entry.stop()
    } catch (err) {
      console.warn('[sessionListeners] closing a listener failed:', err)
    }
  }
  return entries.length
}

/**
 * Release this tab's Firestore: close every tracked listener, then terminate. The ONE way the app terminates
 * Firestore (the cache-rebuild park in backgroundRefresh.js and the reset coordinator's terminate step in main.js),
 * so no listener is left open to hear the shutdown.
 */
export async function releaseFirestore(firestore) {
  closeAllListeners()
  await terminate(firestore)
}

/** How many listeners are open right now (diagnostics and tests). */
export function liveListenerCount() {
  return live.size
}
